import { useEffect, useRef, useState } from 'react'
import ProductCard from './ProductCard'

// Replaces the original Swiper carousel with a native scroll-snap track +
// arrow buttons + clickable pagination dots (no extra dependency).
// Responsive: 1 / 2 / 3 / 4 cards per view, matching the original breakpoints.
//
// Dots represent actual scroll "pages" (one viewport-width of cards each),
// not one dot per product — with 2-4 cards visible at once, a dot-per-item
// scheme leaves trailing dots unreachable since the track can't scroll far
// enough to bring the last few cards to the left edge.
export default function SimilarProducts({ products }) {
  const trackRef = useRef(null)
  const [active, setActive] = useState(0)
  const [pageCount, setPageCount] = useState(1)

  // Single source of truth for both the dots and the arrow buttons — derived
  // from the same `active` page index so they can never disagree (scroll-snap
  // settles on the nearest card, not an exact page-width offset, so computing
  // "at start/end" separately from raw scrollLeft could desync from the dot).
  const atStart = active <= 0
  const atEnd = active >= pageCount - 1

  const recalcPages = () => {
    const el = trackRef.current
    if (!el) return
    const maxScroll = el.scrollWidth - el.clientWidth
    const pages = maxScroll <= 4 ? 1 : Math.ceil(maxScroll / el.clientWidth) + 1
    setPageCount(pages)
    setActive((a) => Math.min(a, pages - 1))
  }

  useEffect(() => {
    recalcPages()
    const el = trackRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(recalcPages)
    observer.observe(el)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products])

  const onScroll = () => {
    const el = trackRef.current
    if (!el) return
    setActive(Math.min(pageCount - 1, Math.round(el.scrollLeft / el.clientWidth)))
  }

  const goTo = (i) => {
    const el = trackRef.current
    if (!el) return
    const maxScroll = el.scrollWidth - el.clientWidth
    el.scrollTo({ left: Math.min(i * el.clientWidth, maxScroll), behavior: 'smooth' })
  }

  const scroll = (dir) => {
    const el = trackRef.current
    if (!el) return
    el.scrollBy({ left: dir * el.clientWidth, behavior: 'smooth' })
  }

  return (
    <section className="py-10 md:py-14 bg-background border-y border-border/10">
      <div className="container px-6 mx-auto">
        <div className="max-w-3xl w-full mx-auto text-center mb-10">
          <span className="text-secondary text-[10px] tracking-[0.3em] uppercase mb-4 block font-normal">DISCOVERY</span>
          <h2 className="text-3xl md:text-5xl font-heading text-primary leading-tight mb-6 font-normal uppercase">
            SIMILAR ITEMS YOU <span className="text-secondary">MIGHT LIKE</span>
          </h2>
          <p className="text-muted leading-relaxed text-lg font-normal uppercase">
            EXPLORE MORE SPIRITUAL ITEMS SYNCHRONIZED WITH YOUR CURRENT SELECTION.
          </p>
        </div>

        <div className="relative">
          {/* Prev / Next arrows */}
          {pageCount > 1 && (
            <>
              <button
                type="button"
                onClick={() => scroll(-1)}
                disabled={atStart}
                aria-label="Previous items"
                className="hidden sm:grid absolute -left-5 top-1/2 -translate-y-1/2 z-10 h-11 w-11 place-items-center rounded-full bg-white text-primary shadow-md border border-gray-100 transition-all hover:bg-secondary hover:text-white active:scale-95 disabled:opacity-30 disabled:pointer-events-none disabled:hover:bg-white disabled:hover:text-primary"
              >
                <i className="fa-solid fa-chevron-left"></i>
              </button>
              <button
                type="button"
                onClick={() => scroll(1)}
                disabled={atEnd}
                aria-label="Next items"
                className="hidden sm:grid absolute -right-5 top-1/2 -translate-y-1/2 z-10 h-11 w-11 place-items-center rounded-full bg-white text-primary shadow-md border border-gray-100 transition-all hover:bg-secondary hover:text-white active:scale-95 disabled:opacity-30 disabled:pointer-events-none disabled:hover:bg-white disabled:hover:text-primary"
              >
                <i className="fa-solid fa-chevron-right"></i>
              </button>
            </>
          )}

          <div
            ref={trackRef}
            onScroll={onScroll}
            className="flex gap-5 overflow-x-auto scrollbar-none snap-x snap-mandatory py-10"
          >
            {products.map((p) => (
              <div
                key={p.id}
                data-slide
                className="snap-start shrink-0 w-full sm:w-[calc((100%-1.25rem)/2)] lg:w-[calc((100%-2.5rem)/3)] xl:w-[calc((100%-3.75rem)/4)]"
              >
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        </div>

        {/* Pagination dots — one per reachable scroll page, not per item */}
        {pageCount > 1 && (
          <div className="flex items-center justify-center gap-2 mt-2">
            {Array.from({ length: pageCount }).map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to page ${i + 1}`}
                onClick={() => goTo(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === active ? 'w-6 bg-secondary' : 'w-1.5 bg-primary/20 hover:bg-primary/40'
                }`}
              ></button>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
